export type ContentStatus =
  | 'Draft'
  | 'Submitted'
  | 'UnderReview'
  | 'ChangesRequested'
  | 'Approved'
  | 'Published'
  | 'Rejected';

export interface AuthorCard {
  authorId: number;
  displayName: string;
  professionalTitle?: string | null;
  biography?: string | null;
  linkedInUrl?: string | null;
  profileImageUrl?: string | null;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  kind: string;
  description?: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface ContentBlock {
  id?: number;
  parentBlockId?: number | null;
  type: string;
  displayOrder: number;
  content?: string | null;
  language?: string | null;
  mediaId?: number | null;
  media?: { id: number; url: string; altText?: string | null; caption?: string | null } | null;
  altText?: string | null;
  caption?: string | null;
  url?: string | null;
  expectedResult?: string | null;
  notes?: string | null;
  commonMistakes?: string | null;
  metadataJson?: string | null;
  children?: ContentBlock[];
}

export interface CourseSection {
  id?: number;
  title: string;
  displayOrder: number;
  blocks: ContentBlock[];
}

export interface ContentReviewRequest {
  requesterName?: string | null;
  requesterEmail?: string | null;
  requesterRole?: string | null;
  submittedAt?: string | null;
  changeSummary?: string | null;
}

export interface CourseListItem {
  id: number;
  title: string;
  slug: string;
  subtitle?: string | null;
  summary?: string | null;
  coverImageUrl?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  categorySlug?: string | null;
  difficulty: string;
  estimatedReadingMinutes: number;
  status: ContentStatus;
  isOwnerApproved: boolean;
  isFeatured: boolean;
  publishedAt?: string | null;
  dateCreated: string;
  author: AuthorCard;
  authors?: AuthorCard[];
  tags: string[];
  relatedCourseId?: number | null;
  estimatedDurationHours?: number;
  displayOrder?: number;
  moduleCount?: number;
  topicCount?: number;
  labCount?: number;
  price?: number;
  discountPercent?: number;
  currency?: string;
  payable?: number;
  isPublic?: boolean;
  maxSeats?: number;
  enrollmentCount?: number;
  seatsRemaining?: number;
  isFull?: boolean;
  averageRating?: number;
  ratingCount?: number;
  reviewRequest?: ContentReviewRequest;
}

export interface RelatedLab {
  id: number;
  name: string;
  slug: string;
  duration?: string | null;
  difficulty: string;
}

export interface KnowledgeTopic {
  id?: number;
  title: string;
  slug?: string;
  objective?: string | null;
  estimatedMinutes: number;
  displayOrder: number;
  blocks: ContentBlock[];
  relatedLabs: RelatedLab[];
  references?: TopicReference[];
}

export interface TopicReference {
  id?: number;
  title: string;
  url: string;
  displayOrder: number;
}

export interface RelatedProject {
  id: number;
  title: string;
  slug: string;
  subtitle?: string | null;
  coverImageUrl?: string | null;
  difficulty: string;
}

export interface AssessmentOption {
  id?: number;
  text: string;
  isCorrect?: boolean | null;
  displayOrder: number;
}

export interface AssessmentQuestion {
  id?: number;
  prompt: string;
  explanation?: string | null;
  displayOrder: number;
  options: AssessmentOption[];
}

export interface ModuleAssessment {
  id?: number;
  title: string;
  description?: string | null;
  passingScore: number;
  displayOrder: number;
  isActive?: boolean;
  questions: AssessmentQuestion[];
  passed?: boolean | null;
  lastScore?: number | null;
}

export interface LabStep {
  id?: number;
  title: string;
  instruction?: string | null;
  expectedResult?: string | null;
  codeSample?: string | null;
  language?: string | null;
  notes?: string | null;
  blocks?: ContentBlock[];
  displayOrder: number;
}

export interface KnowledgeModule {
  id?: number;
  title: string;
  goal?: string | null;
  displayOrder: number;
  topicCount?: number;
  labCount?: number;
  courseCount?: number;
  linkOnly?: boolean;
  author?: AuthorCard | null;
  topics: KnowledgeTopic[];
  assessments?: ModuleAssessment[];
}

export interface CourseDetail extends CourseListItem {
  coverImageId?: number | null;
  reviewNotes?: string | null;
  learningOutcomes?: string | null;
  prerequisites?: string | null;
  sections: CourseSection[];
  modules: KnowledgeModule[];
  projects?: RelatedProject[];
}

export interface CourseMenuItem {
  id: number;
  title: string;
  slug: string;
  subtitle?: string | null;
  displayOrder: number;
  price?: number;
  discountPercent?: number;
  payable?: number;
  isFree?: boolean;
  labs?: CourseMenuItem[];
}

export interface CategoryMenu {
  id: number;
  name: string;
  slug: string;
  displayOrder: number;
  courses: CourseMenuItem[];
  labs?: CourseMenuItem[];
}

export interface LabTopic {
  id?: number;
  title: string;
  slug?: string;
  objective?: string | null;
  displayOrder: number;
  blocks: ContentBlock[];
}

export interface LabModule {
  id?: number;
  title: string;
  goal?: string | null;
  displayOrder: number;
  topics: LabTopic[];
}

export interface LabListItem {
  id: number;
  name: string;
  slug: string;
  subtitle?: string | null;
  description?: string | null;
  coverImageUrl?: string | null;
  categoryName?: string | null;
  categorySlug?: string | null;
  categoryId?: number | null;
  technologyName?: string | null;
  difficulty: string;
  duration?: string | null;
  status: ContentStatus;
  isOwnerApproved: boolean;
  isPublic?: boolean;
  publishedAt?: string | null;
  author: AuthorCard;
  moduleCount: number;
  topicCount: number;
  courseId?: number | null;
  courseTitle?: string | null;
  courseSlug?: string | null;
  moduleId?: number | null;
  moduleTitle?: string | null;
  courseTopicId?: number | null;
  courseTopicTitle?: string | null;
  courseTopicSlug?: string | null;
  averageRating?: number;
  ratingCount?: number;
  reviewRequest?: ContentReviewRequest;
}

export interface LabDetail extends LabListItem {
  coverImageId?: number | null;
  technologyId?: number | null;
  prerequisites?: string | null;
  learningOutcomes?: string | null;
  reviewNotes?: string | null;
  tags: string[];
  modules: LabModule[];
  steps?: LabStep[];
}

export interface ContentDashboard {
  coursesTotal: number;
  coursesDraft: number;
  coursesPendingReview: number;
  coursesPublished: number;
  labsTotal: number;
  labsDraft: number;
  labsPendingReview: number;
  labsPublished: number;
  authorsTotal: number;
  aiGeneratedToday: number;
  aiSuccessToday: number;
  aiFailureToday: number;
  projectsTotal: number;
  projectsDraft: number;
  projectsPendingReview: number;
  projectsPublished: number;
  gigsTotal: number;
  gigsDraft: number;
  gigsPendingReview: number;
  gigsPublished: number;
  eventsTotal: number;
  eventsDraft: number;
  eventsPendingReview: number;
  eventsPublished: number;
}

export interface ProjectFeature {
  id?: number;
  title: string;
  description?: string | null;
  displayOrder: number;
  clientKey?: string;
  epicId?: number | null;
  epicClientKey?: string | null;
}

export interface ProjectEpic {
  id?: number;
  title: string;
  description?: string | null;
  displayOrder: number;
  clientKey?: string;
}

export interface ProjectSectionAsset {
  id?: number;
  sectionKey: string;
  title: string;
  mediaId?: number | null;
  media?: { id: number; url: string; altText?: string | null; contentType?: string; fileName?: string } | null;
  displayOrder: number;
}

export interface ProjectStory {
  id?: number;
  featureId?: number | null;
  featureTitle?: string | null;
  featureClientKey?: string;
  clientKey?: string;
  title: string;
  description?: string | null;
  acceptanceCriteria?: string | null;
  displayOrder: number;
}

export interface ProjectWireframe {
  id?: number;
  title: string;
  description?: string | null;
  mediaId?: number | null;
  media?: { id: number; url: string; altText?: string | null; contentType?: string; fileName?: string } | null;
  displayOrder: number;
}

export interface ProjectErrorMessage {
  id?: number;
  code?: string | null;
  scenario: string;
  message: string;
  displayOrder: number;
}

export interface ProjectListItem {
  id: number;
  title: string;
  slug: string;
  subtitle?: string | null;
  summary?: string | null;
  company: string;
  location?: string | null;
  engagementType: string;
  estimatedHours?: string | null;
  teamType?: string | null;
  techStack?: string | null;
  coverImageUrl?: string | null;
  categoryName?: string | null;
  categoryId?: number | null;
  difficulty: string;
  status: ContentStatus;
  isOwnerApproved: boolean;
  isPublic?: boolean;
  publishedAt?: string | null;
  dateCreated: string;
  author: AuthorCard;
  tags: string[];
  reviewRequest?: ContentReviewRequest;
}

export interface ProjectDetail extends ProjectListItem {
  spec: string;
  specBlocks?: ContentBlock[];
  epicTitle: string;
  epicDescription?: string | null;
  architectureOverview?: string | null;
  architectureBlocks?: ContentBlock[];
  compensation?: string | null;
  applyUrl?: string | null;
  coverImageId?: number | null;
  relatedLabId?: number | null;
  relatedCourseId?: number | null;
  reviewNotes?: string | null;
  epics?: ProjectEpic[];
  features: ProjectFeature[];
  stories: ProjectStory[];
  wireframes: ProjectWireframe[];
  errorMessages: ProjectErrorMessage[];
  sectionAssets?: ProjectSectionAsset[];
  enrollment?: ProjectEnrollment | null;
  mentorRequest?: ProjectMentorRequest | null;
}

export interface ProjectMentorRequest {
  id: number;
  projectId: number;
  projectTitle: string;
  projectSlug: string;
  userProfileId: number;
  userEmail?: string | null;
  rolePreference: string;
  notes?: string | null;
  status: string;
  adminNotes?: string | null;
  dateCreated: string;
}

export interface GeneratedLabStep {
  title: string;
  instruction?: string;
  codeSample?: string;
  language?: string;
  expectedResult?: string;
  notes?: string;
}

export interface GeneratedContent {
  title: string;
  subtitle?: string;
  objective?: string;
  duration?: string;
  prerequisites?: string;
  learningOutcomes?: string[];
  steps?: GeneratedLabStep[];
  sections: { heading: string; blocks: { type: string; content?: string; language?: string }[] }[];
  handsOn?: { objective?: string; steps: { type: string; content?: string }[]; expectedOutcome?: string };
  keyTakeaways: string[];
  commonMistakes: string[];
  references: string[];
  questions?: { prompt: string; explanation?: string; options: { text: string; isCorrect: boolean }[] }[];
}

export interface GeneratedCoursePlan {
  title: string;
  subtitle?: string | null;
  summary?: string | null;
  learningOutcomes?: string | null;
  prerequisites?: string | null;
  difficulty: string;
  estimatedDurationHours: number;
  tags?: string[];
  modules: {
    title: string;
    goal?: string | null;
    displayOrder: number;
    topics: {
      title: string;
      objective?: string | null;
      estimatedMinutes: number;
      displayOrder: number;
      blocks: { type: string; content?: string | null; language?: string | null; altText?: string | null; caption?: string | null; url?: string | null; metadataJson?: string | null }[];
      relatedLabs: { id: number; name: string; difficulty: string; duration?: string | null }[];
    }[];
    assessments: {
      title: string;
      description?: string | null;
      passingScore: number;
      displayOrder: number;
      questions: { prompt: string; explanation?: string; options: { text: string; isCorrect: boolean }[] }[];
    }[];
  }[];
}

export interface CourseEnrollment {
  id: number;
  courseId: number;
  courseSlug: string;
  courseTitle: string;
  price: number;
  discountPercent: number;
  payable: number;
  currency: string;
  isPaid: boolean;
  status: string;
  firstTopicSlug?: string | null;
  dateCreated: string;
  onboardingSource?: string;
  isCourseCompleted?: boolean;
  courseCompletedAt?: string | null;
  completedTopicCount?: number;
  topicCount?: number;
  completedLabCount?: number;
  labCount?: number;
  interviewPackageCount?: number;
  interviewPackageFee?: number;
  courseFeePortion?: number;
}

export interface CourseInterviewPackageTier {
  count: number;
  totalPrice: number;
  perSessionPrice: number;
}

export interface CourseInterviewPackageConfig {
  maxSessions: number;
  allowedCounts: number[];
  packages: CourseInterviewPackageTier[];
  upsellTitle?: string;
  upsellSummary?: string;
  scheduleFirstTip?: string;
  enableInterviewsForCourseEnrollments?: boolean;
  success?: boolean;
}

export interface CatalogSettings {
  defaultMaxSeats: number;
  interviewMaxSeats?: number;
  enableInterviewsForCourseEnrollments: boolean;
  /** When interviews enabled: offer packages up to this count (1–3). */
  maxCourseInterviewSessions?: number;
  interviewDefaultPrice: number;
  interviewDiscountPercent: number;
  interviewPayablePrice?: number;
  reviewPackage1Price: number;
  reviewPackage2Price: number;
  reviewPackage3Price: number;
  trialMonetizationEnabled: boolean;
  trialPriceHint?: string;
}

export interface CourseInvite {
  id: number;
  courseId: number;
  courseTitle: string;
  email: string;
  code?: string | null;
  codeHint: string;
  redeemedAt?: string | null;
  isRedeemed: boolean;
  dateCreated: string;
}

export interface CourseAdminGrant {
  id: number;
  courseId: number;
  courseTitle: string;
  userProfileId: number;
  userEmail?: string | null;
  canEdit: boolean;
  canPublish: boolean;
  canOnboard: boolean;
  canReview: boolean;
}

export interface CourseProgress {
  courseId: number;
  isCourseCompleted: boolean;
  courseCompletedAt?: string | null;
  completedTopicCount: number;
  topicCount: number;
  completedLabCount: number;
  labCount: number;
  completedAssessmentCount?: number;
  assessmentCount?: number;
  completedTopicIds: number[];
  completedLabIds: number[];
  passedAssessmentIds?: number[];
  modules: {
    id: number;
    title: string;
    isCompleted: boolean;
    assessmentRequired?: boolean;
    assessmentPassed?: boolean;
    assessments?: ModuleAssessment[];
    topics: {
      id: number;
      title: string;
      slug: string;
      isCompleted: boolean;
      labs: { id: number; name: string; slug: string; isCompleted: boolean }[];
    }[];
  }[];
}

export interface EnrollmentReportRow {
  enrollmentId: number;
  courseId: number;
  courseTitle: string;
  userProfileId: number;
  userEmail?: string | null;
  mobile?: string | null;
  onboardingSource: string;
  price: number;
  discountPercent: number;
  payable: number;
  isPaid: boolean;
  status: string;
  isCourseCompleted: boolean;
  interviewPackageCount?: number;
  dateCreated: string;
}

export interface ProjectEnrollmentReportRow {
  enrollmentId: number;
  projectId: number;
  projectTitle: string;
  userProfileId: number;
  userEmail?: string | null;
  status: string;
  gitRepoUrl?: string | null;
  submittedAt?: string | null;
  dateCreated: string;
}

export interface GigListItem {
  id: number;
  title: string;
  slug: string;
  subtitle?: string | null;
  summary?: string | null;
  clientName: string;
  duration?: string | null;
  weeklyHours?: string | null;
  techStack?: string | null;
  priceType: 'Hourly' | 'Total' | string;
  priceAmount?: number | null;
  currency: string;
  teamType: 'Individual' | 'Team' | string;
  coverImageUrl?: string | null;
  categoryName?: string | null;
  status: ContentStatus;
  isOwnerApproved: boolean;
  publishedAt?: string | null;
  dateCreated: string;
  author: AuthorCard;
  reviewRequest?: ContentReviewRequest;
}

export interface GigDetail extends GigListItem {
  jobDescription?: string | null;
  whoCanApply?: string | null;
  applyUrl?: string | null;
  coverImageId?: number | null;
  categoryId?: number | null;
  reviewNotes?: string | null;
}

export interface NavMenuItem {
  id?: number;
  menuKey: string;
  label: string;
  path?: string | null;
  isVisible: boolean;
  displayOrder: number;
}

export interface EventListItem {
  id: number;
  title: string;
  slug: string;
  subtitle?: string | null;
  summary?: string | null;
  mode: 'Online' | 'Offline' | string;
  location?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  price: number;
  currency: string;
  coverImageUrl?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  status: ContentStatus;
  isOwnerApproved: boolean;
  publishedAt?: string | null;
  dateCreated: string;
  author: AuthorCard;
  speakerName?: string | null;
  speakerTitle?: string | null;
  speakerCompany?: string | null;
  reviewRequest?: ContentReviewRequest;
}

export interface EventEnrollment {
  id: number;
  eventId: number;
  eventTitle: string;
  eventSlug: string;
  mode: string;
  userProfileId: number;
  userEmail?: string | null;
  status: string;
  price: number;
  payable: number;
  currency: string;
  isPaid: boolean;
  paidAt?: string | null;
  meetingLink?: string | null;
  dateCreated: string;
}

export interface EventDetail extends EventListItem {
  meetingLink?: string | null;
  coverImageId?: number | null;
  categoryId?: number | null;
  reviewNotes?: string | null;
  enrollment?: EventEnrollment | null;
  canSeeMeetingLink: boolean;
}

export interface ProjectEnrollment {
  id: number;
  projectId: number;
  projectTitle: string;
  projectSlug: string;
  company?: string | null;
  userProfileId: number;
  status: string;
  gitRepoUrl?: string | null;
  architectureDocUrl?: string | null;
  setupInstructionsUrl?: string | null;
  submittedAt?: string | null;
  dateCreated: string;
  coverImageUrl?: string | null;
}

export interface GigEnrollment {
  id: number;
  gigId: number;
  gigTitle: string;
  gigSlug: string;
  clientName?: string | null;
  userProfileId: number;
  userEmail?: string | null;
  status: string;
  dateCreated: string;
}

export interface CatalogTreeLab {
  id: number;
  name: string;
  slug: string;
  status: string;
  isActive: boolean;
}

export interface CatalogTreeTopic {
  id: number;
  title: string;
  slug: string;
  isActive: boolean;
  labs: CatalogTreeLab[];
}

export interface CatalogTreeModule {
  id: number;
  title: string;
  isActive: boolean;
  assessmentCount: number;
  topics: CatalogTreeTopic[];
}

export interface CatalogTreeCourse {
  id: number;
  title: string;
  slug: string;
  status: string;
  isActive: boolean;
  authorId?: number;
  authorName?: string | null;
  modules: CatalogTreeModule[];
}

export interface CatalogTreeCategory {
  id: number;
  name: string;
  courses: CatalogTreeCourse[];
  unlinkedLabs?: CatalogTreeLab[];
}

const envIdschoolUrl = (import.meta.env.VITE_IDSCHOOL_URL as string | undefined)?.trim();

function defaultIdschoolOrigin(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:5174';
    }
  }
  return import.meta.env.DEV ? 'http://localhost:5174' : 'https://idschool.in';
}

export const IDSCHOOL_URL = envIdschoolUrl || defaultIdschoolOrigin();

export function idschoolUrl(path = '/programs'): string {
  const origin = IDSCHOOL_URL.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${normalized}`;
}

export const IDSCHOOL_PROGRAMS_URL = idschoolUrl('/programs');
